import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { useToggleState } from '@cian/react-utils';
import cn from 'classnames';
import { useCallback, useMemo } from 'react';
import { EBehavior } from '../../../enums/EBehavior';
import { memoWithDisplayName } from '../../../helpers/memoWithDisplayName';
import { NestedCheckboxesService } from '../../../services/NestedCheckboxesService';
import { SearchLabelService } from '../../../services/SearchLabelService';
import { LabelCheckbox } from '../../LabelCheckbox';
import { prepareCheckboxGroupChangeModel } from '../helpers/prepareCheckboxGroupChangeModel';
import { prepareCheckboxGroupItemChangeModelFactory } from '../helpers/prepareCheckboxGroupItemChangeModelFactory';
import { NestedCheckboxGroupTitleDesktop } from './internal/components/NestedCheckboxGroupTitleDesktop';
import { getNestedCheckboxTheme } from './internal/helpers/getNestedCheckboxTheme';
import styles from './NestedCheckboxGroupDesktop.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var NestedCheckboxGroupDesktop = memoWithDisplayName(function (props) {
  var behavior = props.behavior,
      group = props.group,
      isNestedCheckboxesOpen = props.isNestedCheckboxesOpen,
      searchValue = props.searchValue,
      values = props.values,
      withoutTitle = props.withoutTitle,
      onTitleCheckboxClick = props.onTitleCheckboxClick,
      onNestedCheckboxClick = props.onNestedCheckboxClick,
      renderNestedCheckbox = props.renderNestedCheckbox,
      renderTitleCheckbox = props.renderTitleCheckbox;

  var _useToggleState = useToggleState(),
      isOpenInternal = _useToggleState.state,
      toggleIsOpen = _useToggleState.toggle;

  var nestedCheckboxesService = useMemo(function () {
    return new NestedCheckboxesService(group);
  }, [group]);
  useMemo(function () {
    nestedCheckboxesService.calcFields(searchValue, values);
  }, [nestedCheckboxesService, searchValue, values]);
  var handleTitleCheckboxClick = useCallback(function () {
    switch (behavior) {
      case EBehavior.Multiply:
        {
          var checkboxGroupChangeModel = prepareCheckboxGroupChangeModel(nestedCheckboxesService, values);
          onTitleCheckboxClick(checkboxGroupChangeModel);
          break;
        }

      case EBehavior.Single:
      case EBehavior.SingleInOneGroup:
        toggleIsOpen();
        break;
    }
  }, [behavior, nestedCheckboxesService, onTitleCheckboxClick, toggleIsOpen, values]);
  var handleNestedCheckboxClick = useCallback(function (changeData) {
    var checkboxGroupItemChangeModel = prepareCheckboxGroupItemChangeModelFactory(behavior)(nestedCheckboxesService, values, changeData);
    onNestedCheckboxClick(checkboxGroupItemChangeModel);
  }, [behavior, nestedCheckboxesService, onNestedCheckboxClick, values]);

  if (!nestedCheckboxesService.nestedCheckboxes.length) {
    return null;
  }

  var isOpen = !!withoutTitle || isOpenInternal || !!isNestedCheckboxesOpen;
  var searchLabelService = new SearchLabelService(group.label, searchValue);
  var nestedCheckboxTheme = getNestedCheckboxTheme(behavior, !!withoutTitle);

  var titleView = function () {
    if (withoutTitle) {
      return null;
    }

    var label = searchLabelService.getLabelWithMarks();

    var titleCheckboxView = /*#__PURE__*/_jsx(NestedCheckboxGroupTitleDesktop, {
      behavior: behavior,
      label: label,
      open: isOpen,
      value: nestedCheckboxesService.titledCheckboxValue,
      onCheckboxClick: handleTitleCheckboxClick,
      onToggleClick: toggleIsOpen
    });

    return /*#__PURE__*/_jsx("div", {
      children: renderTitleCheckbox ? renderTitleCheckbox({
        checkboxValue: nestedCheckboxesService.titledCheckboxValue,
        children: titleCheckboxView,
        group: group,
        isOpen: isOpen,
        label: label,
        onChangeVisibility: toggleIsOpen,
        onCheckboxClick: handleTitleCheckboxClick
      }) : titleCheckboxView
    });
  }();

  return /*#__PURE__*/_jsxs("div", {
    className: cn(styles['container'], _defineProperty({}, styles['open'], isOpen)),
    children: [titleView, isOpen && /*#__PURE__*/_jsx("div", {
      className: styles['group'],
      children: nestedCheckboxesService.nestedCheckboxes.map(function (nestedCheckbox, index) {
        var item = nestedCheckbox.item,
            checked = nestedCheckbox.checked,
            label = nestedCheckbox.label;

        var labelCheckboxView = /*#__PURE__*/_jsx(LabelCheckbox, {
          changeData: item,
          checked: checked,
          deviceType: "desktop",
          label: label,
          theme: nestedCheckboxTheme,
          onClick: handleNestedCheckboxClick
        });

        return /*#__PURE__*/_jsx("div", {
          children: renderNestedCheckbox ? renderNestedCheckbox({
            checked: checked,
            children: labelCheckboxView,
            group: group,
            index: index,
            item: item,
            label: label,
            onClick: handleNestedCheckboxClick
          }) : labelCheckboxView
        }, item.value);
      })
    })]
  });
}, 'NestedCheckboxGroupDesktop');